import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { useInspectorRef } from '../../../../hooks/useInspectorRef'
import { Alert, Box, CircularProgress, Paper } from '@mui/material'

import { useInspector } from '../../../../contexts/InspectorContext'
import { api } from '../../../../services/api'
import { downloadBlob } from '../../../../utils/download'
import DataViewTable from '../../../../components/shared/DataViewTable/DataViewTable'
import { variantUtilisationConfig } from '../../../../config/tools/productCatalogueReview/variantUtilisationConfig'
import { overviewConfig } from '../../../../config/tools/productCatalogueReview/overviewConfig'
import { compositeConfig } from '../../../../config/tools/productCatalogueReview/compositeConfig'
import { inactiveConfig } from '../../../../config/tools/productCatalogueReview/inactiveConfig'
import type {
  CatalogueReviewResponse,
  OverviewRow,
} from '../../../../config/tools/productCatalogueReview/types'
import CatalogueHeader from './components/CatalogueHeader'
import CatalogueKpiRow from './components/CatalogueKpiRow'
import CatalogueTabs, { type CatalogueTabIndexes } from './components/CatalogueTabs'
import DuplicatesTab from './components/DuplicatesTab'
import CategoriesTab from './components/CategoriesTab'
import DormantTab, {
  type DormantFilterSnapshot,
} from './components/DormantTab'
import NonSplTab from './components/NonSplTab'

const PERIOD_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Last 30 days' },
  { value: 3, label: 'Last 3 months' },
  { value: 6, label: 'Last 6 months' },
  { value: 12, label: 'Last 12 months' },
  { value: 24, label: 'Last 24 months' },
]

const TAB_SLUGS = [
  'overview',
  'variants',
  'composite',
  'categories',
  'inactive',
  'dormant',
  'duplicates',
  'non-spl',
] as const

const TAB_INDEXES: CatalogueTabIndexes = {
  overview: 0,
  variants: 1,
  composites: 2,
  categories: 3,
  inactive: 4,
  dormant: 5,
  duplicates: 6,
  nonSpl: 7,
}

const LEGACY_TAB_SLUGS: Record<string, (typeof TAB_SLUGS)[number]> = {
  structure: 'overview',
  stock: 'overview',
}

const ProductCatalogueReview: React.FC = () => {
  const { fpCode: fp_code } = useInspectorRef()
  const [searchParams, setSearchParams] = useSearchParams()
  const { franchiseData } = useInspector()

  const [months, setMonths] = useState<number>(() => {
    const m = parseInt(searchParams.get('months') || '', 10)
    return PERIOD_OPTIONS.some((o) => o.value === m) ? m : 12
  })
  const [splOnly, setSplOnly] = useState<boolean>(
    searchParams.get('spl_only') === '1'
  )
  const [tab, setTab] = useState<number>(() => {
    const rawSlug = searchParams.get('tab')
    const slug = rawSlug && LEGACY_TAB_SLUGS[rawSlug] ? LEGACY_TAB_SLUGS[rawSlug] : rawSlug
    const idx = slug ? TAB_SLUGS.indexOf(slug as (typeof TAB_SLUGS)[number]) : -1
    if (idx < 0) return 0
    if (idx === TAB_INDEXES.nonSpl && searchParams.get('spl_only') === '1') return 0
    return idx
  })

  const [data, setData] = useState<CatalogueReviewResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [dormantFilters, setDormantFilters] = useState<DormantFilterSnapshot>({
    focus: 'needs_review',
    search: '',
    categories: [],
    productTypes: [],
    countCompositeAsActivity: true,
  })

  useEffect(() => {
    if (!fp_code) return
    let cancelled = false
    setLoading(true)
    setError(null)
    // include_positive_stock is forced ON so the backend returns the full
    // dormant set; the Dormant tab's FOCUS chips own the stock-band split
    // client-side now.
    const params = new URLSearchParams({
      months: String(months),
      spl_only: splOnly ? 'true' : 'false',
      include_positive_stock: 'true',
    })
    api
      .get(`/inspector/inventory/tools/product-catalogue-review/${fp_code}?${params}`)
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Failed to load data')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fp_code, months, splOnly])

  useEffect(() => {
    if (splOnly && tab === TAB_INDEXES.nonSpl) setTab(0)
  }, [splOnly, tab])

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    next.set('months', String(months))
    if (splOnly) next.set('spl_only', '1')
    else next.delete('spl_only')
    next.delete('include_positive_stock')
    const effectiveTab =
      splOnly && tab === TAB_INDEXES.nonSpl ? 0 : tab
    if (effectiveTab > 0) next.set('tab', TAB_SLUGS[effectiveTab])
    else next.delete('tab')
    setSearchParams(next, { replace: true })
  }, [months, splOnly, tab])

  const handleExport = async () => {
    if (!fp_code || exporting) return
    const params = new URLSearchParams({
      months: String(months),
      spl_only: splOnly ? 'true' : 'false',
      include_positive_stock: 'true',
    })
    // Scope the Dormant sheet to the user's current filter set; other
    // sheets ignore these params (they have no UI filters).
    params.set('dormant_focus', dormantFilters.focus)
    if (dormantFilters.search.trim() !== '') {
      params.set('dormant_search', dormantFilters.search.trim())
    }
    if (dormantFilters.categories.length > 0) {
      params.set('dormant_categories', dormantFilters.categories.join(','))
    }
    if (dormantFilters.productTypes.length > 0) {
      params.set(
        'dormant_product_types',
        dormantFilters.productTypes.join(','),
      )
    }
    params.set(
      'dormant_count_composite_as_activity',
      dormantFilters.countCompositeAsActivity ? 'true' : 'false',
    )
    setExporting(true)
    setError(null)
    try {
      await downloadBlob(
        `/inspector/inventory/tools/product-catalogue-review/export/${fp_code}?${params}`,
        `product-catalogue-review-${fp_code}-${months}mo`,
      )
    } catch (err: any) {
      setError(err?.message || 'Failed to export')
    } finally {
      setExporting(false)
    }
  }

  const franchiseName = franchiseData?.franchise_name || data?.fp_name || fp_code || ''
  const pageTitle = `Product Catalogue Review | ${franchiseName} | Inspector Tool`

  const overviewRows = useMemo<OverviewRow[]>(() => {
    if (!data) return []
    const stockByProductType = new Map(
      data.stock_on_hand.map((r) => [r.product_type, r]),
    )
    return data.product_type_breakdown.map((r) => {
      const stock = stockByProductType.get(r.product_type)
      return {
        product_type: r.product_type,
        product_count: r.product_count,
        sold_product_count: r.sold_product_count,
        units_sold: r.units_sold,
        revenue: r.revenue,
        stock_product_count: stock?.product_count ?? 0,
        units_on_hand: stock?.units_on_hand ?? 0,
        value_on_hand: stock?.value_on_hand ?? 0,
      }
    })
  }, [data])

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Box>
        <CatalogueHeader
          title="Product Catalogue Review"
          periodOptions={PERIOD_OPTIONS}
          months={months}
          onMonthsChange={setMonths}
          splOnly={splOnly}
          onSplOnlyChange={setSplOnly}
          exporting={exporting}
          onExport={handleExport}
          exportDisabled={!data || exporting}
          periodCaption={
            data
              ? `Rolling ${data.period.months}-month window · ${data.period.start} to ${data.period.end_exclusive}`
              : null
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && data && (
          <>
            <CatalogueKpiRow
              summary={data.summary}
              onOpenVariants={() => setTab(TAB_INDEXES.variants)}
              onOpenComposites={() => setTab(TAB_INDEXES.composites)}
            />

            {/* Tier 3 - Connected tabs + table.
              * Use `clipPath` (not `overflow: hidden`) to mask content
              * to the rounded corners. `overflow: hidden` would make
              * this Paper a sticky scrollport for descendants - the
              * DataViewTable's sticky toolbar would then pin at
              * `top: var(--app-chrome-height, 120px)` relative to this
              * Paper instead of the window, producing a ~120 px empty
              * gap above the toolbar and forcing the sticky column
              * header out of its inner card (rounded edges + top
              * border lost, header overlaps the first row). clipPath
              * masks the same corners without registering as a scroll
              * ancestor (see the "Sticky model" note in
              * DataViewTable.tsx), so sticky falls through to
              * the window like every other consumer does. */}
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                clipPath: 'inset(0 round 8px)',
              }}
            >
              <CatalogueTabs
                data={data}
                tab={tab}
                setTab={setTab}
                splOnly={splOnly}
                indexes={TAB_INDEXES}
              />

              {/* Tab content. `p: 2` gives a 16 px gutter on every side
                * so each tab's content is inset evenly from the outer
                * Paper edges - non-filter tabs (Overview / Variants /
                * Composite / Inactive) read with breathing room above
                * the Results toolbar; the Dormant tab's CatalogueFilterPanel
                * sits in the same gutter. */}
              <Box sx={{ p: 2 }}>
                {tab === TAB_INDEXES.overview && (
                  <DataViewTable
                    config={overviewConfig}
                    data={overviewRows}
                    variant="inspector"
                    fpCode={fp_code}
                    embedded
                    toolbarTitle="Product type breakdown"
                    title="Product Catalogue Review - Overview"
                  />
                )}

                {tab === TAB_INDEXES.variants && (
                  <DataViewTable
                    config={variantUtilisationConfig}
                    data={data.variant_utilisation}
                    variant="inspector"
                    fpCode={fp_code}
                    embedded
                    toolbarTitle="Variant utilisation"
                    title="Product Catalogue Review - Variants"
                  />
                )}

                {tab === TAB_INDEXES.composites && (
                  <DataViewTable
                    config={compositeConfig}
                    data={data.composite_depth.items}
                    variant="inspector"
                    fpCode={fp_code}
                    embedded
                    toolbarTitle="Composite products"
                    title="Product Catalogue Review - Composites"
                  />
                )}

                {tab === TAB_INDEXES.categories && fp_code && (
                  <CategoriesTab
                    fp_code={fp_code}
                    title="Product Catalogue Review - Categories"
                  />
                )}

                {tab === TAB_INDEXES.inactive && (
                  <DataViewTable
                    config={inactiveConfig}
                    data={data.inactive_in_use}
                    variant="inspector"
                    fpCode={fp_code}
                    embedded
                    toolbarTitle="Inactive products still in use"
                    title="Product Catalogue Review - Inactive In Use"
                  />
                )}

                {tab === TAB_INDEXES.dormant && (
                  <DormantTab
                    data={data.dormant}
                    fpCode={fp_code}
                    months={months}
                    title="Product Catalogue Review - Dormant"
                    onSnapshotChange={setDormantFilters}
                  />
                )}

                {tab === TAB_INDEXES.duplicates && fp_code && (
                  <DuplicatesTab
                    fp_code={fp_code}
                    title="Product Catalogue Review - Potential Duplicates"
                  />
                )}

                {tab === TAB_INDEXES.nonSpl && !splOnly && (
                  <NonSplTab
                    data={data.non_spl}
                    fpCode={fp_code}
                    title="Product Catalogue Review - Non-PWX Std Prod List"
                  />
                )}
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </>
  )
}

export default ProductCatalogueReview
